import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

export interface Subject {
  id: string;
  label: string;
  slug: string;
  theme: string;
  sortOrder?: number;
}

interface SubjectContextType {
  subjects: Subject[];
  currentSubject: Subject | null;
  setCurrentSubject: (subject: Subject) => void;
  isLoading: boolean;
}

const SubjectContext = createContext<SubjectContextType | undefined>(undefined);

const STORAGE_KEY = "addvance_current_subject";

export function SubjectProvider({ children }: { children: ReactNode }) {
  const { data: dynamicSubjects, isLoading } = useQuery<Subject[]>({
    queryKey: ["/api/subjects"],
  });

  const subjects = dynamicSubjects || [];
  
  const [currentSubject, setCurrentSubjectState] = useState<Subject | null>(null);

  // Synchronize when subjects load
  useEffect(() => {
    if (subjects.length > 0) {
      const saved = localStorage.getItem(STORAGE_KEY);
      let found = subjects[0];
      if (saved) {
        const matching = subjects.find((s) => s.id === saved);
        if (matching) found = matching;
      }
      setCurrentSubjectState(found);
      document.documentElement.className = document.documentElement.className.replace(/theme-\w+/g, "");
      document.documentElement.classList.add(found.theme);
    }
  }, [subjects]);

  const setCurrentSubject = (subject: Subject) => {
    setCurrentSubjectState(subject);
    localStorage.setItem(STORAGE_KEY, subject.id);
    
    // Update document element classes safely
    document.documentElement.className = document.documentElement.className.replace(/theme-\w+/g, "");
    document.documentElement.classList.add(subject.theme);
  };

  return (
    <SubjectContext.Provider value={{ subjects, currentSubject, setCurrentSubject, isLoading }}>
      {children}
    </SubjectContext.Provider>
  );
}

export function useSubject() {
  const context = useContext(SubjectContext);
  if (context === undefined) {
    throw new Error("useSubject must be used within a SubjectProvider");
  }
  return context;
}
